import { pickBy } from "lodash";

export const singleProEndpoint = (id: string) => `/users/${id}`;
export const expertTeamSingleProEndpoint = (id: string) => `/proteam/users/${id}`;
export const expertProjectConfirmationEndpoint = (projectId: string | number, userId: string | number) =>
    `/admin/brand-projects/${projectId}/experts/${userId}/confirmation`;
export const publicProForProject = (token: string, projectId: string | number, proId: string | number) =>
    `/brand-orders/public/${token}/projects/${projectId}/pros/${proId}`;
export const singleProArticlesEndpoint = (id: number) => `/users/${id}/articles`;
export const singleProClassesEndpoint = (id: number) => `/users/${id}/classes`;
export const singleProHowTosEndpoint = (id: number) => `/users/${id}/how-tos`;
export const singleProReviewsEndpoint = (id: number) => `/users/${id}/reviews`;

export const registrationEndpoint = () => `/auth/register`;
export const proRegistrationEndpoint = () => `/auth/register-pro`;

export const loginEndpoint = () => `/auth/login`;

export const getCheckoutSession = (checkoutSessionId: string) => `/stripe/getCheckoutSession/${checkoutSessionId}`;

export const getSubscription = (brandId: number) => `/brand/${brandId}/getSubscription`;

export const myInfo = () => `/users/me`;

export const updateMyInfo = () => `/users/me`;

export const createExpert = () => `/users`;

// DocuSign documents
export const getUserDocuments = (userId: number) => `/users/${userId}/docusign/documents`;
export const getUserSigningUrl = (userId: number) => `/users/${userId}/docusign/signing-url`;
export const downloadUserDocument = (userId: number, envelopeId: string) =>
    `/users/${userId}/docusign/documents/${envelopeId}/download`;
export const sendUserMsa = (userId: number) => `/users/${userId}/docusign/send-msa`;

// Expert SOW envelopes (per selection — sent at Pre-Production entry)
export const getUserSows = (userId: number) => `/users/${userId}/docusign/sows`;
export const getUserSowSigningUrl = (userId: number, envelopeId: string) =>
    `/users/${userId}/docusign/sows/${envelopeId}/signing-url`;
export const downloadUserSow = (userId: number, envelopeId: string) =>
    `/users/${userId}/docusign/sows/${envelopeId}/download`;
// Company countersign (expert team only; two-envelope flow)
export const getSowCountersignUrl = (brandId: number, projectId: number, userId: number) =>
    `/brands/${brandId}/projects/${projectId}/pros/${userId}/sow/countersign-url`;
export const downloadProjectExpertSow = (brandId: number, projectId: number, userId: number) =>
    `/brands/${brandId}/projects/${projectId}/pros/${userId}/sow/download`;

export const searchUserElastic = (searchPhrase: string) => `/search?type=users&q=${searchPhrase}`;
export const searchUser = (searchPhrase: string) => `/users?search=${searchPhrase}`;

// Projects
// NOTE: experts live on the project's SELECTION PROCESS (brand_order_pros), not
// the project. These :projectId-scoped pro endpoints all resolve the project to
// its process server-side, so add/remove/score/rate/rank/opt-in affect EVERY
// project that shares the process (see the useProcessSiblings hook). The paths
// stay project-scoped for backwards compatibility.
export const addToProjectPros = (brandId: number, projectId: number) => `/brands/${brandId}/projects/${projectId}/pros`;
export const getProjectPros = (brandId: number, projectId: number) => `/brands/${brandId}/projects/${projectId}/pros`;
export const sendOptInNotifications = (brandId: number, projectId: number) =>
    `/brands/${brandId}/projects/${projectId}/pros/opt-in-notifications`;
export const createProjectBrief = (brandId: number, projectId: number) =>
    `/brands/${brandId}/projects/${projectId}/briefs`;
export const editProjectBrief = (brandId: number, projectId: number, briefId: number) =>
    `/brands/${brandId}/projects/${projectId}/briefs/${briefId}`;
export const saveProjectBrief = (brandId: number, projectId: number) =>
    `/brands/${brandId}/projects/${projectId}/briefs/save`;
export const saveEditProjectBrief = (brandId: number, projectId: number, briefId: number) =>
    `/brands/${brandId}/projects/${projectId}/briefs/${briefId}/save`;
export const briefAttachments = (brandId: number, projectId: number, briefId: number) =>
    `/brands/${brandId}/projects/${projectId}/briefs/${briefId}/attachments`;
export const briefAttachment = (
    brandId: number,
    projectId: number,
    briefId: number,
    fileId: number,
) => `/brands/${brandId}/projects/${projectId}/briefs/${briefId}/attachments/${fileId}`;
// Brief notes: one thread per brief, shared by every project on its selection
// process. Mounted under /brand-projects (not /brands) so the notes stay open
// after the selection locks at Pre-Production.
export const briefNotes = (projectId: number, briefId: number) =>
    `/brand-projects/${projectId}/briefs/${briefId}/notes`;
export const briefNoteTaggableUsers = (projectId: number, briefId: number) =>
    `/brand-projects/${projectId}/briefs/${briefId}/notes/taggable-users`;
export const commentTaggableUsers = (projectId: number) =>
    `/brand-projects/${projectId}/taggable-users`;
export const briefNote = (projectId: number, briefId: number, noteId: number) =>
    `/brand-projects/${projectId}/briefs/${briefId}/notes/${noteId}`;
export const editProject = (brandId: number, projectId: number) => `/brands/${brandId}/projects/${projectId}`;
export const createBrandProject = (brandId: number) => `/brands/${brandId}/projects`;
export const getBrandProject = (projectId: number) => `/brand-projects/${projectId}`;
export const getBrandProjectAllMessages = (projectId: number) =>
    `/brand-projects/${projectId}/messages`;
// Replace one project-channel instance's option-value selections (per-project overrides of channel defaults)
export const updateProjectChannelOptions = (projectId: number, brandProjectChannelId: number) =>
    `/brand-projects/${projectId}/channels/${brandProjectChannelId}`;
// Algorithm-generated expert × product scores for the project's selection
// process (its single product). Used alongside the human score from
// brand_projects_pros. Shared across every project on the process.
export const getProjectExpertScores = (projectId: number) => `/brand-projects/${projectId}/expert-scores`;
// Lightweight per-(expert, product) scores for the search pool (experts not yet on the project).
export const getProjectExpertProductScores = (projectId: number, userIds: string) =>
    `/brand-projects/${projectId}/expert-product-scores?userIds=${userIds}`;
export const distributionChannelsUrl = `/brand-projects/distribution-channels`;
export const distributionSelections = (projectId: number, groupId: number) =>
    `/brand-projects/${projectId}/group/${groupId}/distribution-selections`;
export const searchBrand = (searchPhrase: string) => `/brands?search=${searchPhrase}`;
export const getBrandProjects = (brandId: number) => `/brands/${brandId}/projects`;
export const deleteBrandProject = (projectId: number) => `/brand-projects/${projectId}`;
export const projectDeleteImpact = (projectId: number) => `/brand-projects/${projectId}/delete-impact`;


export const getBrandOrders = () => `/brand-orders`;
export const getBrandOrdersByBrand = (brandId: number) => `/brands/${brandId}/orders`;
export const getBrandOrder = (orderId: number) => `/brand-orders/${orderId}`;
// Deliverables tracker rows (JSON), or ?format=xlsx for the workbook.
export const orderDeliverablesTracker = (orderId: number) => `/brand-orders/${orderId}/deliverables-tracker`;
export const projectDeliverablesTracker = (projectId: number) =>
    `/brand-projects/${projectId}/deliverables-tracker`;
export const updateSelectionFilmWindow = (orderId: number, orderProId: number) =>
    `/brand-orders/${orderId}/order-pros/${orderProId}/film-window`;
export const createBrandOrder = () => `/brand-orders`;
export const createBrandOrderWithProjects = () => `/brand-orders/with-projects`;
export const parseIntakeDocument = () => `/brand-orders/intake/parse`;
export const matchIntakeProducts = () => `/brand-orders/intake/match-products`;
export const createIntakeOrder = () => `/brand-orders/intake/create`;
export const getProjectFileVersions = (projectId: number, fileId: number) =>
    `/brand-projects/${projectId}/files/${fileId}/versions`;
export const orderProsSync = (orderId: number) => `/brand-orders/${orderId}/order-pros`;
export const updateBrandOrder = (orderId: number) => `/brand-orders/${orderId}`;
export const orderDeleteImpact = (orderId: number) => `/brand-orders/${orderId}/delete-impact`;

export const requestOrderFeedback = (orderId: number) =>
    `/brand-orders/${orderId}/feedback/request`;
export const getOrderFeedback = (orderId: number) =>
    `/brand-orders/${orderId}/feedback`;
export const publicOrderFeedbackForm = (token: string) =>
    `/brand-orders/feedback/public/${token}`;
export const publicSubmitOrderFeedback = (token: string) =>
    `/brand-orders/feedback/public/${token}/submit`;

// Public page endpoints — token-based, no auth required
export const getPublicOrderPage = (token: string) => `/brand-orders/public/${token}`;
export const getPublicProjectPage = (token: string) => `/brand-projects/public/${token}`;
export const publicCreateBrief = (token: string, projectId: number, base = "brand-orders") =>
    `/${base}/public/${token}/projects/${projectId}/briefs`;
export const publicBriefAttachments = (
    token: string,
    projectId: number | string,
    briefId: number,
    base = "brand-orders",
) => `/${base}/public/${token}/projects/${projectId}/briefs/${briefId}/attachments`;
export const publicBriefAttachment = (
    token: string,
    projectId: number | string,
    briefId: number,
    fileId: number,
    base = "brand-orders",
) =>
    `/${base}/public/${token}/projects/${projectId}/briefs/${briefId}/attachments/${fileId}`;
export const publicUpdateBrief = (token: string, projectId: number, briefId: number, base = "brand-orders") =>
    `/${base}/public/${token}/projects/${projectId}/briefs/${briefId}`;
export const publicUpdateExpertStatus = (token: string, projectId: number, proId: number, base = "brand-orders") =>
    `/${base}/public/${token}/projects/${projectId}/pros/${proId}/status`;
export const publicUpdateProjectStatus = (token: string, projectId: number, base = "brand-orders") =>
    `/${base}/public/${token}/projects/${projectId}`;
export const publicCreateComment = (token: string, projectId: number, base = "brand-orders") =>
    `/${base}/public/${token}/projects/${projectId}/comments`;
export const publicUpdateComment = (token: string, projectId: number, commentId: string, base = "brand-orders") =>
    `/${base}/public/${token}/projects/${projectId}/comments/${commentId}`;
export const publicDeleteComment = (token: string, projectId: number, commentId: string, base = "brand-orders") =>
    `/${base}/public/${token}/projects/${projectId}/comments/${commentId}`;
export const publicApproveFile = (token: string, projectId: number, fileId: number, base = "brand-orders") =>
    `/${base}/public/${token}/projects/${projectId}/files/${fileId}/approve`;
export const publicUnapproveFile = (token: string, projectId: number, fileId: number, base = "brand-orders") =>
    `/${base}/public/${token}/projects/${projectId}/files/${fileId}/unapprove`;
export const publicRequestEditFile = (token: string, projectId: number, fileId: number, base = "brand-orders") =>
    `/${base}/public/${token}/projects/${projectId}/files/${fileId}/request-edit`;

// SOW (Statement of Work)
export const getBrandSOWs = () => `/brand-sows`;
export const getBrandSOWsByBrand = (brandId: number) => `/brands/${brandId}/sows`;
export const getBrandSOW = (sowId: number) => `/brand-sows/${sowId}`;
export const createBrandSOW = () => `/brand-sows`;
export const updateBrandSOW = (sowId: number) => `/brand-sows/${sowId}`;
export const deleteBrandSOW = (sowId: number) => `/brand-sows/${sowId}`;

export const searchProName = (searchPhrase: string) => `/users?search=${searchPhrase}&usertype=verified creator`;

// Products
export const searchBrandProducts = (brandId: number, searchPhrase: string) =>
    `/brands/${brandId}/products?search=${searchPhrase}`;

// This is for adding products. It checks against our db which may have existing unbranded products, hopefully cleaning up our data going forward
export const searchUnbrandedProducts = (searchPhrase: string) =>
    `/products?&filterOutBrands=true&search=${searchPhrase}`;

export const createProductForBrand = (brandId: string) => `/brands/${brandId}/product`;

// Flat product-category list for the New/Edit Product category picker (non-admin).
export const getProductCategories = () => `/products/product-categories`;

export const getAllBrandProducts = (brandId: number) => `/brands/${brandId}/products`;

export const getAllBrandExperts = (brandId: number) => `/brands/${brandId}/pros`;

export const getBrandProducts = (
    brandId: number,
    limit: number,
    page: number = 1,
    searchPhrase: string = "",
    // Catalog filters (TDC-1499): the account's crawled category (subtree
    // match, deep links only), one of its sub brands (brands_rmn_brands
    // suppliers), and the TDC taxonomy (subtree match — the dropdown).
    rmnCategoryId: string = "",
    rmnBrandId: string = "",
    productCategoryId: string = ""
) =>
    `/brands/${brandId}/products?limit=${limit}&page=${page}&search=${searchPhrase}${
        rmnCategoryId ? `&rmn_category_id=${rmnCategoryId}` : ''
    }${rmnBrandId ? `&rmn_brand_id=${rmnBrandId}` : ''}${
        productCategoryId ? `&product_category_id=${productCategoryId}` : ''
    }`;

export const getProductForBrand = (brandId: string, productId: string) => `/brands/${brandId}/product/${productId}`;

// This update actually just posts to the create, and if the name already exists we connect it to the brand
export const updateProductForBrand = (brandId: string, productId: string) => `/brands/${brandId}/product/${productId}`;

// This update actually updates
export const patchProductForBrand = (brandId: string, productId: string) => `/brands/${brandId}/product/${productId}`;

export const deleteProductForBrand = (brandId = 0, productId = 0) => `/brands/${brandId}/products/${productId}`;

export const deleteProductAdmin = (desireId: number | string) => `/admin/desires/${desireId}`;

// Embeds
export const createEmbed = () => `/embeds`;

export const getBrandEmbeds = (
    brandId: number,
    limit: number,
    page: number = 1,
    searchPhrase: string = "",
    productId: string = ""
) => `/brands/${brandId}/embeds?search=${searchPhrase}&productId=${productId}&limit=${limit}&page=${page}`;

export const getEmbed = (embedId: number) => `/embeds/${embedId}`;

export const updateEmbed = (embedId: number) => `/embeds/${embedId}`;

export const updateEmbedSettings = (embedId: number) => `/embeds/${embedId}/settings`;

export const deleteEmbed = (embedId: number) => `/embeds/${embedId}`;

export const getFonts = () => `/fonts`;

// Add Video To Embed
export const addVideoToEmbed = () => `/embeds/addVideoToEmbed`;

export const removeVideoFromEmbed = (embedId: number, videoId: number) => `/embeds/${embedId}/video/${videoId}`;

// Brand
export const getBrandByVideoId = (videoId: string) => `/files/${videoId}/brand`;

export const removeBrandProEndpoint = (brandId: string, userId: string) => `/brands/${brandId}/pros/${userId}`;

export const getBrandTeamEndpoint = (brandId: string) => `/brands/${brandId}/team`;

export const addBrandTeamMemberEndpoint = (brandId: number) => `/brands/${brandId}/team/member`;

export const updateBrandTeamMemberEndpoint = (brandId: number, userId: number) =>
    `/brands/${brandId}/team/member/${userId}`;

export const removeBrandTeamEndpoint = (brandId: string, userId: string) => `/brands/${brandId}/team/${userId}`;

export const changeBrandTeamAdmin = (brandId: string, userId: string) => `/brands/${brandId}/team/admin/${userId}`;

export const postRetrieveSubscriptionUrl = (brandId: string) => `/brands/${brandId}/subscription/url`;

// Notifications
export const getNotifications = (limit = 500, offset = 0) => `notifications?limit=${limit}&offset=${offset}`;

export const getNotificationsSettings = () => `notifications/settings`;

export const deleteNotification = (id: number) => `notifications/${id}`;

// Activity log — one endpoint serves every scope via query params. Pass an
// anchor (brand_project_id / brand_order_id / s3_file_id / brand_id) for a
// scoped feed, or none for the global admin feed.
export interface ActivityLogQuery {
    brand_project_id?: number | string;
    brand_order_id?: number | string;
    s3_file_id?: number | string;
    brand_id?: number | string;
    actor_user_id?: number | string;
    category?: string; // comma-separated
    severity?: string; // 'major' | 'minor'
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
}

export const getActivityLog = (params: ActivityLogQuery = {}) => {
    const qs = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join("&");
    return `activity-log${qs ? `?${qs}` : ""}`;
};

// Notification-kind taxonomy (severity/category/keepInbox) from the backend
// ACTIVITY_REGISTRY — see getActivityKinds. The settings screen uses it to badge
// log-only kinds instead of duplicating severity in the frontend.
export const getActivityKinds = () => `activity-log/kinds`;
export const clearNotifications = () => `notifications`;

// Professions
export const ProfessionsEndpoint = () => `/professions`;
export const SingleProfessionEndpoint = (professionId: number) => `/professions/${professionId}`;
export const getProfessionTree = () => "/professions/tree";
export const SingleSubProfessionEndpoint = (subProfessionId: number) =>
    `/professions/subprofessions/${subProfessionId}`;
// Areas of Focus (sub-sub-professions). Create is scoped to the L1 profession.
export const SubSubProfessionsEndpoint = (professionId: number) => `/professions/${professionId}/subsubprofessions`;
export const SingleSubSubProfessionEndpoint = (subSubProfessionId: number) =>
    `/professions/subsubprofessions/${subSubProfessionId}`;

// Admin
// export const getAllBrandProjects = () => `admin/brand-projects`;
export const getAllBrandProjects = () => `/brand-projects`;
export const getShortlistEligibleProjects = () => `/brand-projects/shortlist-eligible`;

// Videos
export const getVideoEndpoint = (brandId: string, videoType: string, data?: any) => {
    const {
        pagination: { offset = 0, limit = 20 },
        ...payload
    } = data;
    const mode = videoType === "UGC" ? "reviews" : "s3files";
    const params = {
        offset: offset.toString(),
        limit: limit.toString(),
        ...payload
    };
    const queryString = new URLSearchParams(pickBy(params, (v: any) => !!v)).toString();

    // mode       = s3Files / reviews
    // videoType  = UGC / XGC / TDC
    return `/brands/${brandId}/${mode}/${videoType}?${queryString}`;
};

// Get Brand Videos via Projects (Project-based: Brand → Projects → S3Files)
export const getBrandProjectVideosEndpoint = (brandId: string, data?: any) => {
    const {
        pagination: { offset = 0, limit = 20 },
        ...payload
    } = data;
    const params = {
        offset: offset.toString(),
        limit: limit.toString(),
        ...payload
    };
    const queryString = new URLSearchParams(pickBy(params, (v: any) => !!v)).toString();
    return `/brands/${brandId}/videos?${queryString}`;
};

// Get Single UGC
export const getVideoUGC = (brandId: number, reviewId: number) => `/brands/${brandId}/review/UGC/${reviewId}`;

export const deleteVideoUGC = (brandId: number, reviewId: number) => `/brands/${brandId}/review/UGC/${reviewId}`;

// Get Single XGC || TDC
export const getVideoFile = (brandId: number, fileId: number) => `/brands/${brandId}/s3file/${fileId}`;

export const getVideoFileV2 = (fileId: number) => `/files/${fileId}`;

// Totals + per-day buckets, counted server-side (TDC-1676). params:
// from, to (YYYY-MM-DD), tz (IANA), project, source.
export const getVideoFileAnalytics = (brandId: number, fileId: number, params = "") =>
    `/brands/${brandId}/s3file/${fileId}/analytics?${params}`;

export const setProjectVideosApproval = (brandId: number, projectId: number) =>
    `/brands/${brandId}/projects/${projectId}/videos/approval`;

export const setOrderVideosApproval = (orderId: number) => `/brand-orders/${orderId}/videos/approval`;

export const unapproveBrandProjectFile = (brandId: number, projectId: number, fileId: number) =>
    `/brands/${brandId}/projects/${projectId}/file/${fileId}/unapprove`;

export const getBrandProjectFileMessages = (projectId: number, s3FileId: number) =>
    `/brand-projects/${projectId}/${s3FileId}/messages`;
// A video's comment thread through a share link (TDC-1690).
export const publicFileMessages = (base: string, token: string, projectId: number, fileId: number) =>
    `/${base}/public/${token}/projects/${projectId}/files/${fileId}/messages`;
// A published video's comment thread on the order share page (/orders/share/:id).
export const orderShareFileMessages = (orderId: number, projectId: number, fileId: number) =>
    `/brand-orders/${orderId}/share/projects/${projectId}/files/${fileId}/messages`;

export const deleteVideoFile = (brandId: number, fileId: number) => `/brands/${brandId}/s3file/${fileId}`;

export const zipFiles = () => `brands/files/zip`;

export const signedDownload = () => `brands/files/signed-download`;

export const getVideoEmbeds = (fileId: number, params = "") => `/files/getembeds/${fileId}?${params}`;

// Forgot Password
export const postForgotPassword = () => "auth/reset-password";

export const postResetPassword = () => "auth/update-password";
export const postCreateAutoLoginRedirect = () => "auth/create-auto-login-redirect";
export const postUseAutoLoginRedirect = () => "auth/use-auto-login-redirect";
export const getUserToken = (userid: string) => `/admin/userToken/${userid}`;

export const getUserBySlugProTeam = (slug: string) => `/proteam/users/slug/${slug}`;

export const getUserByIdProTeam = (id: number) => `/proteam/users/${id}`;

export const postProfileEndpoint = `/users/me/field`;

export const getSignedRequestEndpoint = (name: string, bucket: string) =>
    `/files/getsignedrequest?name=${name}&bucket=${bucket}`;

export const createUserProTeam = () => `/proteam/users`;

export const updateUserProTeam = (userId: number) => `/proteam/users/${userId}`;

export const getStaffUsersByType = (usertype: string) =>
    `/proteam/users?usertype=${encodeURIComponent(usertype)}&limit=500`;

export const updateVideoSettingsEndpoint = (videoId: number) => `/files/${videoId}`;
export const updateVideoTranscriptEndpoint = (videoId: number) => `/files/${videoId}/transcript`;
export const incrementThumbnailEndpoint = (videoId: number | string) => `/files/${videoId}/thumbnail`;
// Server-built presigned PUT into a pipeline bucket (final video, thumbnail,
// transcript); the API derives bucket, key and content type from the file.
export const uploadRequestEndpoint = () => `/files/upload-request`;

// Account Manager Admin

export const createProduct = () => `/accountmanager/products`;
// Product with its brands (id, name, rmn) — the PDP URL brand picker.
export const getProductDetail = (desireId: number | string) => `/desires/${desireId}`;

export const updateProduct = (productId: number) => `/accountmanager/products/${productId}`;

export const listProjectPdpUrls = (projectId: number | string) =>
    `/brand-projects/${projectId}/pdp-urls`;

export const publicListProjectPdpUrls = (
    token: string,
    projectId: number | string,
    base = "brand-orders"
) => `/${base}/public/${token}/projects/${projectId}/pdp-urls`;

// TDC-1722 brief brand assets: what a new brief pre-fills with (brand assets +
// per-product disclaimers), and the upload that returns a file URL only.
export const projectBriefAssets = (projectId: number | string) =>
    `/brand-projects/${projectId}/brief-assets`;
export const projectBriefAssetUpload = (projectId: number | string, kind: string) =>
    `/brand-projects/${projectId}/brief-assets/${kind}`;
export const publicProjectBriefAssets = (
    token: string,
    projectId: number | string,
    base = "brand-orders"
) => `/${base}/public/${token}/projects/${projectId}/brief-assets`;
export const publicProjectBriefAssetUpload = (
    token: string,
    projectId: number | string,
    kind: string,
    base = "brand-orders"
) => `/${base}/public/${token}/projects/${projectId}/brief-assets/${kind}`;
export const brandBriefAssetUpload = (brandId: number | string, kind: string) =>
    `/brands/${brandId}/brief-assets/${kind}`;

export const listProductPdpUrls = (productId: number, brandId?: number) =>
    `/accountmanager/products/${productId}/pdp-urls${brandId ? `?brandId=${brandId}` : ""}`;

export const createProductPdpUrl = (productId: number) =>
    `/accountmanager/products/${productId}/pdp-urls`;

export const updateProductPdpUrl = (productId: number, pdpUrlId: number) =>
    `/accountmanager/products/${productId}/pdp-urls/${pdpUrlId}`;

export const deleteProductPdpUrl = (productId: number, pdpUrlId: number) =>
    `/accountmanager/products/${productId}/pdp-urls/${pdpUrlId}`;

export const rescoreProductPdpUrl = (productId: number, pdpUrlId: number) =>
    `/accountmanager/products/${productId}/pdp-urls/${pdpUrlId}/score`;

// Score history + brand-user-safe async rescore (TDC-1499 phase 3).
// listProductPdpUrlScores -> ready runs, chronological, no report blobs;
// getProductPdpUrlScore -> one run incl the stored audit report;
// requestProductPdpUrlScore -> async enqueue (force), poll via latest_attempt.
export const listProductPdpUrlScores = (productId: number, pdpUrlId: number) =>
    `/accountmanager/products/${productId}/pdp-urls/${pdpUrlId}/scores`;

export const getProductPdpUrlScore = (productId: number, pdpUrlId: number, scoreId: number) =>
    `/accountmanager/products/${productId}/pdp-urls/${pdpUrlId}/scores/${scoreId}`;

export const requestProductPdpUrlScore = (productId: number, pdpUrlId: number) =>
    `/accountmanager/products/${productId}/pdp-urls/${pdpUrlId}/request-score`;
// Product Object JSON-LD from the product record — needs no video on the
// row. POST, synchronous; returns { id, product_json_ld, product_json_ld_validation }.
export const generateProductPdpUrlJsonLd = (productId: number, pdpUrlId: number) =>
    `/accountmanager/products/${productId}/pdp-urls/${pdpUrlId}/product-json-ld`;
// One of our videos on a page (desires_pdp_url_videos, TDC-1644): GET the
// link, PUT { video_file_url?, thumbnail_url_override?, upload_date_override? }
// to put the video on the page or edit its details there (upsert; omitted
// fields keep their value, "" clears), DELETE to take it off.
export const productPdpUrlVideo = (productId: number, pdpUrlId: number, fileId: number) =>
    `/accountmanager/products/${productId}/pdp-urls/${pdpUrlId}/videos/${fileId}`;
// Consumer Voice per product (Run CV Analysis on the row): POST {brandId},
// GET history, GET one (with report).
export const productConsumerVoiceEndpoint = (productId: number) =>
    `/accountmanager/products/${productId}/consumer-voice`;
export const productConsumerVoiceSettingsEndpoint = (productId: number) =>
    `/accountmanager/products/${productId}/consumer-voice/settings`;
export const productConsumerVoiceItemEndpoint = (productId: number, auditId: number, brandId?: number | null) =>
    `/accountmanager/products/${productId}/consumer-voice/${auditId}${brandId ? `?brandId=${brandId}` : ""}`;
// Product FAQs (desires_faqs, synced from the page checks) and the experts'
// answers under each.
export const productFaqsEndpoint = (productId: number) => `/accountmanager/products/${productId}/faqs`;
export const productFaqsScanEndpoint = (productId: number) => `/accountmanager/products/${productId}/faqs/scan`;
export const productFaqEndpoint = (productId: number, faqId: number) => `/accountmanager/products/${productId}/faqs/${faqId}`;
export const productFaqAnswersEndpoint = (productId: number, faqId: number) => `/accountmanager/products/${productId}/faqs/${faqId}/answers`;
// AI Presence (lib/aiPresence) — same product surface as Consumer Voice.
export const productAiPresenceEndpoint = (productId: number) =>
    `/accountmanager/products/${productId}/ai-presence`;
export const productAiPresenceSettingsEndpoint = (productId: number) =>
    `/accountmanager/products/${productId}/ai-presence/settings`;
export const productAiPresencePromptsEndpoint = (productId: number) =>
    `/accountmanager/products/${productId}/ai-presence/prompts`;
export const productAiPresenceItemEndpoint = (productId: number, auditId: number, brandId?: number | null) =>
    `/accountmanager/products/${productId}/ai-presence/${auditId}${brandId ? `?brandId=${brandId}` : ""}`;
// Every AI Presence item endpoint has a sibling /responses (the prompt-level
// answers): insert it before the query string, keep the query.
export const aiPresenceResponsesEndpoint = (itemEndpoint: string, promptKey?: string | null) => {
    const [path, query] = itemEndpoint.split("?");
    const params = new URLSearchParams(query || "");
    if (promptKey) params.set("prompt_key", promptKey);
    const qs = params.toString();
    return `${path}/responses${qs ? `?${qs}` : ""}`;
};

// PDP Autopilot (lib/controllers/ccos.js): the product bundle the Autopilot
// and Timeline tabs render, the rules re-run, per-product objectives, and
// the recommendation / intervention row endpoints.
const withBrand = (brandId?: number | null) => (brandId ? `?brandId=${brandId}` : '');
export const productAutopilotEndpoint = (productId: number, brandId?: number | null) =>
    `/accountmanager/products/${productId}/autopilot${withBrand(brandId)}`;
export const productAutopilotRefreshEndpoint = (productId: number, brandId?: number | null) =>
    `/accountmanager/products/${productId}/autopilot/refresh${withBrand(brandId)}`;
export const productAutopilotObjectivesEndpoint = (productId: number, brandId?: number | null) =>
    `/accountmanager/products/${productId}/autopilot/objectives${withBrand(brandId)}`;
// Put one product page on autopilot / take it off: PATCH { enabled }.
export const productAutopilotPageEndpoint = (productId: number, pdpUrlId: number, brandId?: number | null) =>
    `/accountmanager/products/${productId}/autopilot/pages/${pdpUrlId}${withBrand(brandId)}`;
// Put one of the product's videos on autopilot / take it off: PATCH { enabled }.
export const productAutopilotVideoEndpoint = (productId: number, fileId: number, brandId?: number | null) =>
    `/accountmanager/products/${productId}/autopilot/videos/${fileId}${withBrand(brandId)}`;
// Switch channel / category / placement nodes for a video on a product: PATCH { selections: [...] }.
export const productAutopilotVideoSelectionsEndpoint = (productId: number, fileId: number, brandId?: number | null) =>
    `/accountmanager/products/${productId}/autopilot/videos/${fileId}/selections${withBrand(brandId)}`;
export const productPageChangeEndpoint = (productId: number, brandId?: number | null) =>
    `/accountmanager/products/${productId}/interventions/page-change${withBrand(brandId)}`;
// The observation log for one page (every look: analysis, probe, tag), and an
// on-demand probe (internal).
export const productAutopilotPageObservationsEndpoint = (productId: number, pdpUrlId: number, brandId?: number | null, limit = 50) =>
    `/accountmanager/products/${productId}/autopilot/pages/${pdpUrlId}/observations${withBrand(brandId)}${brandId ? '&' : '?'}limit=${limit}`;
export const productAutopilotPageProbeEndpoint = (productId: number, pdpUrlId: number, brandId?: number | null) =>
    `/accountmanager/products/${productId}/autopilot/pages/${pdpUrlId}/probe${withBrand(brandId)}`;
export const recommendationEndpoint = (id: number) => `/accountmanager/recommendations/${id}`;
// The client acts, we detect: accept = "I'll do this" (the page is then
// probed daily until the change is seen); unaccept = "Not doing this".
export const recommendationAcceptEndpoint = (id: number) => `/accountmanager/recommendations/${id}/accept`;
export const recommendationUnacceptEndpoint = (id: number) => `/accountmanager/recommendations/${id}/unaccept`;
export const recommendationAddToStrategyEndpoint = (id: number) =>
    `/accountmanager/recommendations/${id}/add-to-strategy`;
export const interventionEndpoint = (id: number) => `/accountmanager/interventions/${id}`;
// "I removed it": an accepted removal expectation the next scrape confirms.
export const interventionExpectRemovalEndpoint = (id: number) => `/accountmanager/interventions/${id}/expect-removal`;
// Prediction model runs (admin): the gate table, the per-target serve switch, unpublish.
export const autopilotModelRunsEndpoint = (limit = 30) => `/accountmanager/autopilot/model-runs?limit=${limit}`;
export const autopilotModelRunEndpoint = (id: number) => `/accountmanager/autopilot/model-runs/${id}`;
export const autopilotModelRunTargetEndpoint = (id: number, key: string) =>
    `/accountmanager/autopilot/model-runs/${id}/targets/${encodeURIComponent(key)}`;
export const autopilotModelRunUnpublishEndpoint = (id: number) => `/accountmanager/autopilot/model-runs/${id}/unpublish`;
// Manual promotion: the state machine's recent executions, import one, promote an imported run.
export const autopilotModelRunExecutionsEndpoint = (limit = 20) => `/accountmanager/autopilot/model-runs/executions?limit=${limit}`;
export const autopilotModelRunImportEndpoint = () => '/accountmanager/autopilot/model-runs/import';
export const autopilotModelRunPromoteEndpoint = (id: number) => `/accountmanager/autopilot/model-runs/${id}/promote`;
export const brandAutopilotSummaryEndpoint = (brandId: number) => `/accountmanager/brands/${brandId}/autopilot/summary`;

export const importBrandVideos = (brandId: number) => `/accountmanager/brands/${brandId}/import/init`;
// Google Search Console connection per brand (DNS TXT verification):
// GET list, POST {domain}, POST verify, DELETE property.
export const brandSearchConsoleEndpoint = (brandId: number) => `/accountmanager/brands/${brandId}/search-console`;
export const brandSearchConsolePropertyEndpoint = (brandId: number, propId: number) =>
    `/accountmanager/brands/${brandId}/search-console/${propId}`;
export const brandSearchConsoleVerifyEndpoint = (brandId: number, propId: number) =>
    `/accountmanager/brands/${brandId}/search-console/${propId}/verify`;
// Settings > Onboarding: the Shopify custom pixel (GET state, POST generate
// the token, DELETE disconnect), the tab's status (GET: tag + pixel last
// reported) and the install check (POST {url?}).
export const brandShopifyPixelEndpoint = (brandId: number) => `/accountmanager/brands/${brandId}/shopify`;
export const brandOnboardingStatusEndpoint = (brandId: number) => `/accountmanager/brands/${brandId}/onboarding`;
export const brandOnboardingCheckEndpoint = (brandId: number) => `/accountmanager/brands/${brandId}/onboarding/check`;
// Dashboard To Do list: the items that apply to the brand and whether each is done.
export const brandChecklistEndpoint = (brandId: number) => `/accountmanager/brands/${brandId}/checklist`;
export const importProjectVideos = (projectId: number) => `/accountmanager/projects/${projectId}/import/init`;
export const reimportFile = (fileId: number) => `/files/${fileId}/import/init`;

export const getDiversityData = "/users/pros/demographics/diversity";
export const getProfessionData = "/users/pros/demographics/professions";
export const getStateData = "/users/pros/demographics/state";
export const getProspects = "/users/pros?status=Prospect";

export const dashboardGeneralEndpoint = "/internal-dashboard/general";
export const dashboardPipelineEndpoint = "/internal-dashboard/pipeline";
export const dashboardExpertsEndpoint = "/internal-dashboard/experts";
export const dashboardFulfillmentEndpoint = "/internal-dashboard/fulfillment";
export const dashboardShootLocationsEndpoint = "/internal-dashboard/shoot-locations";
export const dashboardSalesEndpoint = "/internal-dashboard/sales";
export const dashboardSearchEndpoint = "/internal-dashboard/search";
export const dashboardProductCategoriesEndpoint = "/internal-dashboard/product-categories";
export const dashboardProductSubCategoriesEndpoint = "/internal-dashboard/product-sub-categories";
export const dashboardProductSubSubCategoriesEndpoint = "/internal-dashboard/product-sub-sub-categories";
export const dashboardSubProfessionsEndpoint = "/internal-dashboard/sub-professions";
export const dashboardAreasOfFocusEndpoint = "/internal-dashboard/areas-of-focus";

export const jiraRoadmapEndpoint = "/jira/roadmap";
export const jiraBugsEndpoint = "/jira/bugs";
export const jiraBugAttachmentEndpoint = (issueKey: string) => `/jira/bugs/${issueKey}/attachments`;

export const qrCodeEndpoint = "/admin/qr_codes";

export const getAdminMetrics = "/admin/categories/metrics";
export const postCategory = "/admin/categories";

// Expert-matching admin surface (internal-only). The backend gates these on
// internal staff roles; the portal additionally gates the pages behind the
// INTERNAL route guard. See application/src/api/routes/admin.js.
export const adminProductCategories = "/admin/product-categories";
export const adminAttributes = "/admin/attributes";
export const adminAttributeValues = "/admin/attribute-values";

// Per-entity attribute-option assignment (GET current ids / PUT new id list).
export const adminProductCategoryAttributeValues = (id: number) => `/admin/product-categories/${id}/attribute-values`;
export const adminSubProfessionAttributeValues = (id: number) => `/admin/sub-professions/${id}/attribute-values`;
export const adminSubSubProfessionAttributeValues = (id: number) => `/admin/sub-sub-professions/${id}/attribute-values`;
export const adminScoresSettings = "/admin/scores/settings";
export const adminScoresStatus = "/admin/scores/status";
export const adminScoresRecompute = "/admin/scores/recompute";
export const adminScoresDistribution = "/admin/scores/distribution";
// Expert IQ → Algorithm demo: a product's top experts + per-attribute "why".
export const adminProductBestMatches = (desireid: number, limit = 6) =>
    `/admin/scores/products/${desireid}/best-matches?limit=${limit}`;
export const adminCategoryBestMatches = (categoryId: number, limit = 6) =>
    `/admin/scores/categories/${categoryId}/best-matches?limit=${limit}`;
export const adminUrlBestMatches = "/admin/scores/demo/url";

// Affinity network (Expert IQ → Affinity Net tab)
export const adminAffinityCategories = "/admin/affinity/categories";
export const adminAffinitySubProfessionGraph = (id: number) => `/admin/affinity/sub-professions/${id}/graph`;
export const adminAffinityCategoryGraph = (id: number) => `/admin/affinity/category-graph/${id}`;
export const adminAffinityProfessionGraph = (id: number) => `/admin/affinity/professions/${id}/graph`;
export const adminAffinityProductGraph = (id: number) => `/admin/affinity/products/${id}/graph`;
export const adminAffinityProductSearch = (search: string) =>
    `/admin/affinity/products?search=${encodeURIComponent(search)}`;
export const adminAffinityLinks = "/admin/affinity/links";
export const adminAffinityLinkDelete = (subProfessionId: number, productCategoryId: number) =>
    `/admin/affinity/links/${subProfessionId}/${productCategoryId}`;

// Attribute network (Expert IQ → Attribute Net tab)
export const adminAttrNetSubProfessions = "/admin/attribute-network/sub-professions";
export const adminAttrNetSubSubProfessions = "/admin/attribute-network/sub-sub-professions";
export const adminAttrNetCategoryGraph = (id: number) => `/admin/attribute-network/product-categories/${id}/graph`;
export const adminAttrNetSubProfessionGraph = (id: number) => `/admin/attribute-network/sub-professions/${id}/graph`;
export const adminAttrNetSubSubProfessionGraph = (id: number) =>
    `/admin/attribute-network/sub-sub-professions/${id}/graph`;
export const adminAttrNetAttributeValueGraph = (id: number) => `/admin/attribute-network/attribute-values/${id}/graph`;
export const adminAttrNetLinks = "/admin/attribute-network/links";
export const adminAttrNetLinkDelete = (entityType: string, entityId: number, attributeValueId: number) =>
    `/admin/attribute-network/links/${entityType}/${entityId}/${attributeValueId}`;

// Shortlists
export const createShortlist = () => `/shortlists`;
export const addExpertToShortlist = (shortlistId: number) => `/shortlists/${shortlistId}/experts`;
export const duplicateShortlist = (shortlistId: number, name?: string) =>
    `/shortlists/${shortlistId}/duplicate${name ? `?name=${name}` : ""}`;

// Video Shortlists
export const videoShortlistsEndpoint = `/video-shortlists`;
export const videoShortlistById = (id: number) => `/video-shortlists/${id}`;
export const videoShortlistVideos = (id: number) => `/video-shortlists/${id}/videos`;
export const addVideoToVideoShortlist = (shortlistId: number) => `/video-shortlists/${shortlistId}/videos`;
export const removeVideoFromVideoShortlist = (shortlistId: number, s3FileId: number) =>
    `/video-shortlists/${shortlistId}/videos/${s3FileId}`;
export const videoShortlistVideosOrder = (shortlistId: number) =>
    `/video-shortlists/${shortlistId}/videos/order`;
export const duplicateVideoShortlist = (shortlistId: number, name?: string) =>
    `/video-shortlists/${shortlistId}/duplicate${name ? `?name=${name}` : ""}`;

export const deleteVideo = (videoId: string) => `/videos/${videoId}`;
// What deleting the video takes off live retailer pages: { pdp_videos, pdp_pages }.
export const videoDeleteImpact = (videoId: number) => `/videos/${videoId}/delete-impact`;

export const getIconikFileTranscript = (fileId: string) => `/iconik/file/${fileId}/transcript`;

// Lightweight DB-transcript read — no JSON-LD generation side effects.
export const getFileTranscript = (fileId: number) => `/files/${fileId}/transcript`;
export const getVideoTags = (fileId: number) => `/files/${fileId}/tags`;
export const videoTagVocabulary = () => `/files/tags/vocabulary`;
export const getVideoTag = (tagId: string) => `/files/tags/${tagId}`;

export const getFileIconikJsonLd = (
    fileId: number,
    opts?: { pdpUrlId?: number; regenerate?: boolean },
) => {
    const params = new URLSearchParams();
    if (opts?.pdpUrlId) params.set("pdpUrlId", String(opts.pdpUrlId));
    if (opts?.regenerate) params.set("regenerate", "true");
    const qs = params.toString();
    return `/files/${fileId}/iconik/jsonld${qs ? `?${qs}` : ""}`;
};

export const requestFileTranscript = (fileId: number) => `/files/${fileId}/iconik/transcript`;

export const auditPdpEndpoint = `/public/audit/pdp`;
export const auditPdpReportEmailEndpoint = `/public/audit/report/email`;
// Consumer Voice (Reddit-sourced sentiment): async create + token poll.
export const auditConsumerVoiceEndpoint = `/public/audit/consumer-voice`;
export const auditConsumerVoiceStatusEndpoint = (id: number, token?: string | null) =>
    `/public/audit/consumer-voice/${id}${token ? `?t=${encodeURIComponent(token)}` : ""}`;
export const auditAiPresenceEndpoint = `/public/audit/ai-presence`;
export const auditAiPresenceStatusEndpoint = (id: number, token?: string | null) =>
    `/public/audit/ai-presence/${id}${token ? `?t=${encodeURIComponent(token)}` : ""}`;


// Proposal wizard ("Choose Your Path") — public config/preview/create + lead
// share-token view/edit/email + admin list/detail/edit
export const proposalWizardConfigEndpoint = `/public/proposals/config`;
export const proposalPricingPreviewEndpoint = `/public/proposals/preview`;
export const createProposalEndpoint = `/public/proposals`;
export const proposalSuggestEndpoint = `/public/proposals/suggest`;
export const proposalExamplesEndpoint = (categoryId: number, contentTypeIds?: number[], channelIds?: number[]) =>
    `/public/proposals/examples?product_category_id=${categoryId}${
        contentTypeIds && contentTypeIds.length ? `&content_type_ids=${contentTypeIds.join(",")}` : ""
    }${channelIds && channelIds.length ? `&channel_ids=${channelIds.join(",")}` : ""}`;
export const salesDemoDefaultsEndpoint = `/admin/sales/demo-defaults`;
export const publicProposalDetailEndpoint = (token: string) => `/public/proposals/${token}`;
export const publicProposalEmailEndpoint = (token: string) => `/public/proposals/${token}/email`;
export const salesProposalsEndpoint = `/admin/sales/proposals`;
export const salesProposalDetailEndpoint = (id: number | string) => `/admin/sales/proposals/${id}`;
export const salesProposalConvertEndpoint = (id: number | string) => `/admin/sales/proposals/${id}/convert`;

// Content Types
export const getContentTypes = (expertConnect?: boolean) =>
    `/content-types${expertConnect ? "?expertConnect=true" : ""}`;
export const getContentTypesByBrand = (brandId: number) => `/content-types/brand/${brandId}`;

// Content Channels (the 6 generic channels)
export const getContentChannels = () => `/content-channels`;
export const getChannelsByContentType = (contentTypeId: number) => `/content-channels/by-content-type/${contentTypeId}`;

// Content Placements (catalog of specific placements, each mapped to a channel)
export const getContentPlacements = () => `/content-placements`;
export const getContentPlacementCategories = () => `/content-placement-categories`;

// Brand
export const getBrand = (brandId: number) => `/brands/${brandId}`;

// API Keys
// Internal staff pass a brand id to see one brand's keys (brand detail page).
export const getApiKeys = (brandId?: number) =>
  brandId ? `/settings/api-keys?brand_id=${brandId}` : `/settings/api-keys`;
export const createApiKey = () => `/settings/api-keys`;
export const deleteApiKey = (keyId: string) => `/settings/api-keys/${keyId}`;

// Personal API Keys (service account keys) — the caller's own keys for apps they build
// (internal staff only for now).
export const getServiceKeys = () => `/settings/service-keys`;
export const createServiceKey = () => `/settings/service-keys`;
export const deleteServiceKey = (keyId: string) => `/settings/service-keys/${keyId}`;

// Pricing
export const pricingAll = "/pricing/all";
export const pricingLicenses = "/pricing/licenses";
export const pricingPackages = "/pricing/packages";
export const pricingResource = (resource: string) => `/pricing/${resource}`;
export const pricingResourceItem = (resource: string, id: number) => `/pricing/${resource}/${id}`;
// Live pricing preview for an unsaved draft order (POST).
export const pricingPreview = "/pricing/preview";
// Brand subscription plan catalog (read: internal; patch: pricing editors).
export const pricingPlansEndpoint = "/pricing/plans";
export const pricingPlanEndpoint = (key: string) => `/pricing/plans/${key}`;
// Plan comparison table for the internal /pricing page (active + public plans).
export const pricingPlanCatalogEndpoint = "/pricing/plans/catalog";
// Usage rate card: unit cost × buffer ÷ (1 − margin) = customer unit price.
export const pricingUsageRatesEndpoint = "/pricing/usage-rates";
export const pricingUsageRateEndpoint = (kind: string) => `/pricing/usage-rates/${kind}`;
// Per-brand usage (counts, our cost, customer price) — admin only.
export const adminBrandsUsageEndpoint = "/admin/brands/usage";
export const adminBrandUsageEndpoint = (id: number) => `/admin/brands/${id}/usage`;
// One row per ledger event (audit / score / report synthesis / prep pack).
export const adminBrandUsageEventsEndpoint = (id: number) => `/admin/brands/${id}/usage/events`;
// Internal Brands page cards: Consumer Voice + Brand Intelligence runs.
export const adminBrandConsumerVoiceEndpoint = (id: number) => `/admin/brands/${id}/consumer-voice`;
// The brand CV run form's product picker.
export const adminBrandConsumerVoiceTargetsEndpoint = (id: number) => `/admin/brands/${id}/consumer-voice/targets`;
export const adminBrandConsumerVoiceItemEndpoint = (id: number, auditId: number) =>
    `/admin/brands/${id}/consumer-voice/${auditId}`;
export const adminBrandAiPresenceEndpoint = (id: number) => `/admin/brands/${id}/ai-presence`;
export const adminBrandAiPresenceTargetsEndpoint = (id: number) => `/admin/brands/${id}/ai-presence/targets`;
export const adminBrandAiPresenceItemEndpoint = (id: number, auditId: number) =>
    `/admin/brands/${id}/ai-presence/${auditId}`;
export const adminBrandPrepPacksEndpoint = (id: number) => `/admin/brands/${id}/prep-packs`;

// Sales admin
export const salesLeadsEndpoint = `/admin/sales/leads`;
export const salesLeadDeleteEndpoint = (id: number) => `/admin/sales/leads/${id}`;
export const salesLeadsCsvEndpoint = `/admin/sales/leads/export.csv`;
export const salesPdpAuditReportsEndpoint = `/admin/sales/pdp-audit-reports`;
export const salesPdpAuditReportsJsonEndpoint = `/admin/sales/pdp-audit-reports/export.json`;
// Ad-hoc Consumer Voice runs from the public tool — Sales ▸ Consumer Voice Reports.
export const salesConsumerVoiceReportsEndpoint = `/admin/sales/consumer-voice-reports`;
export const salesConsumerVoiceReportEndpoint = (id: number) => `/admin/sales/consumer-voice-reports/${id}`;
export const salesAiPresenceReportsEndpoint = `/admin/sales/ai-presence-reports`;
export const salesAiPresenceReportEndpoint = (id: number) => `/admin/sales/ai-presence-reports/${id}`;
export const salesPrepPacksEndpoint = `/admin/sales/prep-packs`;
export const salesPrepPackEndpoint = (id: number) => `/admin/sales/prep-packs/${id}`;
export const salesPrepPackRegenerateEndpoint = (id: number) => `/admin/sales/prep-packs/${id}/regenerate`;

// Brand portal (/portal/:brandSlug) — any brand's workspace; rmn:true
// networks add the white-label login.
// Public identity for the branded login/theming (no auth; rmn:true only).
export const publicRmnBrandBySlugEndpoint = (slug: string) => `/public/rmn/${slug}`;
// Authenticated slug resolver for logged-in viewers (any active brand; no
// association graph).
export const brandBySlugEndpoint = (slug: string) => `/brands/slug/${slug}`;
// Authenticated RMN-scoped tools (rmnControl-gated on the API).
export const rmnAuditPdpEndpoint = '/rmn/audit/pdp';
// The workspace's plan + product meter (count vs max_products, cadence).
export const rmnPlanEndpoint = '/rmn/plan';
// Counts behind the Catalog | Discovered toggles (products + brands pairs).
export const rmnCatalogCountsEndpoint = '/rmn/catalog-counts';
export const rmnAuditReportsEndpoint = '/rmn/audit/reports';
export const rmnAuditReportEndpoint = (id: number) => `/rmn/audit/reports/${id}`;
export const rmnAuditCategoryReportEndpoint = '/rmn/audit/category-report';
export const rmnAuditBrandReportEndpoint = '/rmn/audit/brand-report';
export const rmnAuditComparisonReportEndpoint = '/rmn/audit/comparison-report';
export const rmnPrepPacksEndpoint = '/rmn/prep-packs';
export const rmnPrepPackEndpoint = (id: number) => `/rmn/prep-packs/${id}`;
export const rmnPrepPackRegenerateEndpoint = (id: number) => `/rmn/prep-packs/${id}/regenerate`;
export const rmnBrandsEndpoint = '/rmn/brands';
// Manual brand-page URL override on the network link (RMN roots only).
export const rmnBrandEndpoint = (id: number) => `/rmn/brands/${id}`;
// Consumer Voice: brand-row run + network-scoped history/detail.
export const rmnBrandConsumerVoiceEndpoint = (id: number) => `/rmn/brands/${id}/consumer-voice`;
export const rmnBrandConsumerVoiceTargetsEndpoint = (id: number) => `/rmn/brands/${id}/consumer-voice/targets`;
export const rmnConsumerVoiceReportsEndpoint = '/rmn/consumer-voice/reports';
export const rmnConsumerVoiceReportEndpoint = (id: number) => `/rmn/consumer-voice/reports/${id}`;
export const rmnBrandAiPresenceEndpoint = (id: number) => `/rmn/brands/${id}/ai-presence`;
export const rmnBrandAiPresenceTargetsEndpoint = (id: number) => `/rmn/brands/${id}/ai-presence/targets`;
export const rmnAiPresenceReportsEndpoint = '/rmn/ai-presence/reports';
export const rmnAiPresenceReportEndpoint = (id: number) => `/rmn/ai-presence/reports/${id}`;
// One-click Brand Intelligence from a Catalog > Brands row.
export const rmnBrandPrepPacksEndpoint = (id: number) => `/rmn/brands/${id}/prep-packs`;
export const rmnProductsEndpoint = '/rmn/products';
// Gemini-file one catalog product into the network's own category tree
// (Catalog > Products "Categorize" — rows with no network category).
export const rmnProductCategorizeEndpoint = (desireid: number) =>
    `/rmn/products/${desireid}/categorize`;
export const rmnProposalConfigEndpoint = '/rmn/proposals/config';
export const rmnProposalPreviewEndpoint = '/rmn/proposals/preview';
export const rmnProposalsEndpoint = '/rmn/proposals';
export const rmnProposalEndpoint = (id: number) => `/rmn/proposals/${id}`;
export const rmnProposalConvertEndpoint = (id: number) => `/rmn/proposals/${id}/convert`;
export const rmnCategoriesEndpoint = '/rmn/categories';
// Manual category URL edit (PATCH { url }; empty clears to a placeholder).
export const rmnCategoryEndpoint = (id: number) => `/rmn/categories/${id}`;
// Client catalog import: discovered-products/brands review + promotion
// (network-scoped; brand review/promote is RMN-roots-only server-side).
// Lists take match_status/search/page/limit → {rows, count, page, limit}.
// The products list also filters by brand_id (a CATALOG brand, or 'none'
// for rows still awaiting brand identification).
export const rmnDiscoveredProductsEndpoint = '/rmn/discovered-products';
// Still-discovered brands with product counts — the Discovered Products
// page's "All discovered brands" filter (rmn_discovered_brand_id).
export const rmnDiscoveredBrandsEndpoint = `/rmn/discovered-brands`;
export const rmnDiscoveredBrandsReviewEndpoint = '/rmn/discovered-brands/review';
export const rmnDiscoveredProductsAutoMatchEndpoint = '/rmn/discovered-products/auto-match';
// URL-first catalog adds (POST { url }). Category/brand launch a bounded
// crawl (async → { crawl_id }; results appear when it finishes; 409 =
// a crawl is already running); product imports the single page
// synchronously (→ { desireid, title, created }, can take ~a minute).
export const rmnImportCategoryUrlEndpoint = '/rmn/categories/import-url';
export const rmnImportBrandUrlEndpoint = '/rmn/brands/import-url';
export const rmnImportProductUrlEndpoint = '/rmn/products/import-url';
// Re-crawl an existing category's subtree (crawl only, no report).
export const rmnCategoryCrawlEndpoint = (id: number) => `/rmn/categories/${id}/crawl`;
export const rmnBrandCrawlEndpoint = (id: number) => `/rmn/brands/${id}/crawl`;
// The imported products a brand report can audit (the form's picker).
export const rmnBrandReportTargetsEndpoint = (id: number) =>
    `/rmn/brands/${id}/report-targets`;
// The products under a category a category report can audit (its picker) —
// imported and still-discovered alike, keyed by an opaque `key`.
export const rmnCategoryReportTargetsEndpoint = (id: number) =>
    `/rmn/categories/${id}/report-targets`;
// Graceful stop: the crawler stops fetching new pages but still processes
// everything it already found (identification passes, imports, reports).
export const rmnCrawlStopEndpoint = (id: number) => `/rmn/crawls/${id}/stop`;
// Network crawl status (active + finished in the last 10 min) — feeds the
// Catalog pages' "crawl running" placeholder rows.
export const rmnCrawlsEndpoint = `/rmn/crawls`;
export const rmnPromoteDiscoveredProductEndpoint = (id: number) => `/rmn/discovered-products/${id}/promote`;
export const rmnIgnoreDiscoveredProductEndpoint = (id: number) => `/rmn/discovered-products/${id}/ignore`;
export const rmnPromoteDiscoveredBrandEndpoint = (id: number) => `/rmn/discovered-brands/${id}/promote`;
export const rmnIgnoreDiscoveredBrandEndpoint = (id: number) => `/rmn/discovered-brands/${id}/ignore`;
// Internal admin: crawl + categories + discovery review.
export const adminRmnCrawlsEndpoint = `/admin/rmn/crawls`;
export const adminRmnCrawlEndpoint = (id: number) => `/admin/rmn/crawls/${id}`;
export const adminRmnCrawlCancelEndpoint = (id: number) => `/admin/rmn/crawls/${id}/cancel`;
export const adminRmnCrawlResumeEndpoint = (id: number) => `/admin/rmn/crawls/${id}/resume`;
export const adminRmnCrawlGraphEndpoint = (id: number) => `/admin/rmn/crawls/${id}/graph`;
export const adminRmnClearCrawlDataEndpoint = (brandId: number) => `/admin/rmn/brands/${brandId}/crawl-data`;
export const adminRmnBrandCategoriesEndpoint = (brandId: number) => `/admin/rmn/brands/${brandId}/categories`;
export const adminRmnCategoryEndpoint = (id: number) => `/admin/rmn/categories/${id}`;
export const adminRmnDiscoveredProductsEndpoint = (brandId: number) =>
    `/admin/rmn/brands/${brandId}/discovered-products`;
export const adminRmnDiscoveredBrandsEndpoint = (brandId: number) => `/admin/rmn/brands/${brandId}/discovered-brands`;
export const adminRmnAutoMatchEndpoint = (brandId: number) =>
    `/admin/rmn/brands/${brandId}/discovered-products/auto-match`;
export const adminRmnPromoteProductEndpoint = (id: number) => `/admin/rmn/discovered-products/${id}/promote`;
export const adminRmnIgnoreProductEndpoint = (id: number) => `/admin/rmn/discovered-products/${id}/ignore`;
export const adminRmnPromoteBrandEndpoint = (id: number) => `/admin/rmn/discovered-brands/${id}/promote`;
export const adminRmnIgnoreBrandEndpoint = (id: number) => `/admin/rmn/discovered-brands/${id}/ignore`;

export const expertProductTestingEndpoint = (projectId: number | string) =>
    `/expert/projects/${projectId}/product-testing`;
export const expertProductTestingSaveEndpoint = (projectId: number | string, desireid: number) =>
    `/expert/projects/${projectId}/product-testing/${desireid}`;
export const projectProductTestingSubmissionsEndpoint = (projectId: number | string) =>
    `/brand-projects/${projectId}/product-testing`;

export const brandOrderFeedbackLink = (orderId: number) =>
    `/brand-orders/${orderId}/feedback/link`;
